'use client';

import { useEffect, useState } from 'react';

import { ScrollToApplyButton } from '#/components/ApplyButton/ScrollToApplyButton';
import { VolopDetailsList } from '#/components/DetailsList/Volop/VolopDetailsList';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { EmbedSearchWidgetApiMetadata } from '#/modules/embedSearchWidget/api/types/EmbedSearchWidgetApiMetadata';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewActionButtons } from '#/modules/listing/components/ListingView/ActionButtons/ListingViewActionButtons';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { listingTiming } from '#/modules/listing/helpers/listingTiming';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';
import { getVolopPublishedDate } from '#/modules/listing/volops/helpers/getVolopPublishedDate';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { EmbedSearchWidgetViewVolopPageAtsApply } from './EmbedSearchWidgetViewVolopPageAtsApply';
import { EmbedSearchWidgetViewVolopPageSimpleApply } from './EmbedSearchWidgetViewVolopPageSimpleApply';

type Props = {
  embedSearchWidget: EmbedSearchWidgetApiMetadata;
  volop: ApiVolop;
  now: DatetimeString;
};

export function EmbedSearchWidgetViewVolopPage({
  embedSearchWidget,
  volop,
  now,
}: Props) {
  const { i18n } = useI18n();
  const [applied, setApplied] = useState(false);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: volop.id,
  });

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts: volop.hasAts,
      listingType: 'VOLOP',
      listingId: volop.id,
      listingName: volop.name,
      orgId: volop.org?.id,
      orgName: volop.org?.name,
      orgType: volop.org?.orgType,
    });
  }, [volop]);

  const orgId = volop.org?.id ?? null;
  const orgLogo = volop.org?.logo ?? null;
  const orgName = volop.org?.name ?? null;
  const orgType = volop.org?.orgType ?? null;

  const timing = listingTiming({
    now,
    publishedDate: getVolopPublishedDate(volop),
    expires: volop.expires,
    isPreview: false,
  });

  return (
    <ListingViewBody>
      <ListingViewSidebar maxWidth={288}>
        <ListingViewSidebarImage
          orgLogo={orgLogo}
          orgName={orgName}
          orgURLForCurrentLocale={null}
        />

        <ListingViewSidebarTitle
          address={volop.address}
          listingName={volop.name}
          listingType="VOLOP"
          orgName={orgName}
          orgUrl={null}
          remoteOptions={volop.remoteOptions}
        />

        <ListingViewSidebarContent
          timing={timing}
          applied={applied}
          listingType="VOLOP"
          showScrollToApplyButton
          listingViewStatus={ListingViewStatus.PUBLISHED}
        />

        <ListingViewSidebarLinks searchContext={searchContext} orgUrl={null} />
      </ListingViewSidebar>

      <ListingViewMain>
        {searchContext && (
          <ListingViewHeaderSearchContext searchContext={searchContext} />
        )}

        <ListingViewHeader
          address={volop.address}
          isPostedAnonymously={volop.isPostedAnonymously}
          listingId={volop.id}
          listingName={volop.name}
          listingType="VOLOP"
          timing={timing}
          orgName={orgName}
          orgType={orgType}
          orgUrl={null}
          remoteOptions={volop.remoteOptions}
          canBeDoneInADay={volop.canBeDoneInADay}
          actionButtons={
            <ListingViewActionButtons>
              <ScrollToApplyButton
                applied={applied}
                eventName="I Want to Help Button on Volop Listing"
              >
                {i18n('I Want to Help')}
              </ScrollToApplyButton>
            </ListingViewActionButtons>
          }
        />

        <ListingViewSection title={i18n('Details')} noBorderTop>
          <VolopDetailsList
            areasOfFocus={volop.areasOfFocus}
            startDate={volop.startDate}
            startTime={volop.startTime}
            endDate={volop.endDate}
            endTime={volop.endTime}
            timezone={volop.timezone}
            driversLicenseRequired={volop.driversLicenseRequired}
            backgroundCheckRequired={volop.backgroundCheckRequired}
            orientationRequired={volop.orientationRequired}
            feeRequired={volop.feeRequired}
            feeAmount={volop.feeAmount}
            isRecurring={volop.isRecurring}
            welcomeFamilies={volop.welcomeFamilies}
            welcomeTeens={volop.welcomeTeens}
            welcomeGroups={volop.welcomeGroups}
            welcomeAge55Plus={volop.welcomeAge55Plus}
            welcomeIntl={volop.welcomeIntl}
            welcomePrivateCorpGroups={volop.welcomePrivateCorpGroups}
            detailsStipendProvided={volop.detailsStipendProvided}
            detailsTrainingProvided={volop.detailsTrainingProvided}
            detailsHousingAvailable={volop.detailsHousingAvailable}
            detailsCulturalSupport={volop.detailsCulturalSupport}
            detailsAcademicCreditAvailable={
              volop.detailsAcademicCreditAvailable
            }
            detailsWheelchairAccessible={volop.detailsWheelchairAccessible}
            timesOfDay={volop.timesOfDay}
            expectedTime={volop.expectedTime}
            ageRequirement={volop.ageRequirement}
            otherRequirements={volop.otherRequirements}
            numVolunteersNeeded={volop.numVolunteersNeeded}
            commitmentDetails={volop.commitmentDetails}
          />
        </ListingViewSection>

        <ListingViewDescriptionSection
          listingName={volop.name}
          image={volop.image}
          description={volop.description}
          descriptionTruncated={volop.descriptionTruncated}
          showMoreLinkTitle={i18n('Read more about this volunteer opportunity')}
          onToggleDetails={(isOpen) => {
            if (!isOpen) return;

            trackClicked('read more about this listing', {
              listing_type: 'VOLOP',
              locale: volop.locale,
            });
          }}
        />

        <ListingViewLocationSection
          listingType="VOLOP"
          address={volop.address}
          directions={volop.directions}
          remoteOptions={volop.remoteOptions}
        />

        <ListingViewApplySection
          title={
            embedSearchWidget.headline ?? i18n('Please fill out this form')
          }
        >
          {embedSearchWidget.topImage && (
            <FilestackImage
              data={embedSearchWidget.topImage}
              width={800}
              height={200}
            />
          )}

          {volop.hasAts ? (
            <EmbedSearchWidgetViewVolopPageAtsApply
              embedSearchWidget={embedSearchWidget}
              volopId={volop.id}
              applied={applied}
              applyText={volop.applyText}
              requestedAttachments={volop.requestedAttachments}
              requestedAttachmentsHash={volop.requestedAttachmentsHash}
              orgId={orgId}
              orgType={orgType}
              onApplied={() => setApplied(true)}
            />
          ) : (
            <EmbedSearchWidgetViewVolopPageSimpleApply
              embedSearchWidget={embedSearchWidget}
              volopId={volop.id}
              applied={applied}
              onApplied={() => setApplied(true)}
              orgId={orgId}
              orgType={orgType}
              requestedAttachmentsHash={volop.requestedAttachmentsHash}
            />
          )}
        </ListingViewApplySection>

        {searchContext && (
          <PrintHidden>
            <div style={{ marginTop: 70, marginBottom: 48 }}>
              <SearchContextHeader searchContext={searchContext} noDivider />
            </div>
          </PrintHidden>
        )}
      </ListingViewMain>
    </ListingViewBody>
  );
}
